import { Context, type Effect } from 'effect';

import type { Cart as CartView } from './cartSchema';
import type { CartError, CartWriteError } from './errors';

/**
 * What an add asks for: which cart, which variant, how many.
 *
 * `quantity` is what to ADD, not what the line should end up at. A shop that
 * already holds this variant merges the two, and neither the caller nor the
 * port can predict the result — which is one reason every write answers the
 * cart the shop then states.
 */
export type AddCartItem = {
  readonly token: string;
  readonly variantCode: string;
  readonly quantity: number;
};

/**
 * What a quantity change asks for: which cart, which line, how many in total.
 *
 * `quantity` is the line's new quantity, not a delta. `itemId` addresses the
 * line, and it comes from the cart the port last answered.
 */
export type SetCartItemQuantity = {
  readonly token: string;
  readonly itemId: string;
  readonly quantity: number;
};

/** What a removal asks for: which cart, which line. */
export type RemoveCartItem = {
  readonly token: string;
  readonly itemId: string;
};

/** What applying a coupon asks for: which cart, which code. */
export type ApplyCartCoupon = {
  readonly token: string;
  readonly code: string;
};

/** What removing a coupon asks for: which cart. There is at most one coupon. */
export type RemoveCartCoupon = {
  readonly token: string;
};

/**
 * A shopper's cart: read it, start one, and change what is in it.
 *
 * **Every write answers the cart the shop states afterwards.** That is the
 * port's central promise and it is not a convenience. The backends here answer
 * a write with the order's totals and no lines at all, so a caller given an
 * outcome or nothing would have to read the cart again to render anything — and
 * nothing in an outcome tells the caller that the read is mandatory. Returning
 * the resource puts that read in the provider, which is the one place that
 * knows whether this backend needs it. A tenant whose write answers the whole
 * cart makes one call instead of two, and this type does not change.
 *
 * The rule has no exception. `create` answers a cart that is empty by
 * definition, and it still answers the cart the shop states, so there is one
 * sentence to remember rather than one sentence and a footnote.
 *
 * **The cart's token is a parameter, not an ambient service.** It addresses a
 * resource — it is a path segment on every one of these endpoints — so it says
 * WHICH cart, and that is the caller's to say. A customer's credentials are the
 * other kind of identity and travel the other way: the client the provider was
 * built with sets them on the request, and they never appear here.
 *
 * That choice is what lets `create` exist at all. An ambient token would make
 * `create` write the new token into a session store, which would put a session
 * side effect inside a capability provider. Here `create` answers a cart
 * carrying its token, and whoever owns the session writes it down.
 *
 * `create` is an `Effect` value rather than a function returning one, for the
 * reason `CatalogueMenu.roots` is: it takes no arguments, and a thunk over a
 * call with no arguments describes nothing more than the call.
 *
 * A tenant provides this tag when its backend serves all five operations. One
 * that reads carts but cannot change them does not provide it, and every page
 * that writes a cart fails to compile — which is the point of one tag per
 * capability rather than one service with optional methods.
 */
export type CartService = {
  readonly cart: (token: string) => Effect.Effect<CartView, CartError>;
  readonly create: Effect.Effect<CartView, CartWriteError>;
  readonly addItem: (input: AddCartItem) => Effect.Effect<CartView, CartWriteError>;
  readonly setItemQuantity: (input: SetCartItemQuantity) => Effect.Effect<CartView, CartWriteError>;
  readonly removeItem: (input: RemoveCartItem) => Effect.Effect<CartView, CartWriteError>;
  readonly applyCoupon: (input: ApplyCartCoupon) => Effect.Effect<CartView, CartWriteError>;
  readonly removeCoupon: (input: RemoveCartCoupon) => Effect.Effect<CartView, CartWriteError>;
};

export class Cart extends Context.Service<Cart, CartService>()('@engine/Capability/Cart') {}
